import { CloudFrontCookiesKey, CloudfrontSignedCookiesOutput, GcpCdnCookiesKey, GcpCdnSignedCookiesOutput } from '@/types';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { CookieSetOptions } from 'universal-cookie';
import * as Sentry from '@sentry/browser';
/** remove leading and trailing white spaces and line terminators and also any leading slashes (/) from input string */
const removeLeadingSlashes = (input: string) => (input ?? '').trim().replace(/^\/+/, '');

export const getCloudfrontUrl = (filepath: string) => {
  const relativePathEncoded = removeLeadingSlashes(filepath);

  try {
    return new URL(relativePathEncoded, import.meta.env.VITE_APP_CDN_BASE_URL).href;
  } catch (error) {
    console.error('cloudfront.ts -> getCloudfrontUrl() ->', error);
    console.error('cloudfront.ts -> getCloudfrontUrl() -> filePath ->', filepath);
    return `${import.meta.env.VITE_APP_CDN_BASE_URL}/${relativePathEncoded}`;
  }
};

export const useFetchCloudfrontSignedCookies = () => {
  const getCloudfrontSignedCookieOptions = useCallback(
    (path?: string): CookieSetOptions => ({
      httpOnly: false,
      secure: true,
      sameSite: 'lax',
      // NOTE: must ensure domain for the cookie is a subdomain for both cloudfront CDN & frontend or browser rejects
      // see: https://stackoverflow.com/questions/41990841/how-to-use-cloudfront-signed-cookies-in-the-browser
      // and see https://stackoverflow.com/a/30384487
      // domain: import.meta.env.VITE_APP_CDN_BASE_URL.split('.').slice(1).join('.'),
      domain: '.abyssfabric.co', // Temporary fix for cross-subdomain cookie sharing
      ...(path && { path }),
    }),
    []
  );

  const { getAccessTokenSilently, user } = useAuth0();
  const cloudFrontCookieNames = Object.values(CloudFrontCookiesKey);
  const gcpCdnCookieNames = Object.values(GcpCdnCookiesKey);
  const allCookieNames = [...cloudFrontCookieNames, ...gcpCdnCookieNames];
  const [, setCookie] = useCookies(allCookieNames);

  const fetchCookies = useCallback(async () => {
    let cookies: Record<string, CloudfrontSignedCookiesOutput | GcpCdnSignedCookiesOutput> = {};
    try {
      const token = await getAccessTokenSilently();
      cookies = await (
        await fetch(`${import.meta.env.VITE_APP_API_URL}/cloudfront-cookies`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        })
      ).json();

      const firstCookie = Object.values(cookies)[0];

      // Check if this is GCP CDN format (single cookie)
      if (firstCookie && 'Cloud-CDN-Cookie' in firstCookie) {
        // GCP CDN format - single cookie (no URL encoding for GCP CDN)
        setCookie(
          GcpCdnCookiesKey.CloudCdnCookie,
          firstCookie['Cloud-CDN-Cookie'],
          getCloudfrontSignedCookieOptions('/')
        );
      } else if (firstCookie && 'CloudFront-Key-Pair-Id' in firstCookie) {
        // AWS CloudFront format - multiple cookies
        setCookie(
          CloudFrontCookiesKey.KeyPair,
          firstCookie['CloudFront-Key-Pair-Id'],
          getCloudfrontSignedCookieOptions('/')
        );

        Object.entries(cookies).forEach(([path, cookie]) => {
          // individual policy / signature cookies scoped per base CDN path
          setCookie(
            CloudFrontCookiesKey.Policy,
            (cookie as CloudfrontSignedCookiesOutput)['CloudFront-Policy'],
            getCloudfrontSignedCookieOptions(path)
          );
          setCookie(
            CloudFrontCookiesKey.Signature,
            (cookie as CloudfrontSignedCookiesOutput)['CloudFront-Signature'],
            getCloudfrontSignedCookieOptions(path)
          );
        });
      }

      return cookies;
    } catch {
      return {};
    }
  }, [getAccessTokenSilently, getCloudfrontSignedCookieOptions, setCookie]);
  const { VITE_APP_API_URL, NODE_ENV } = import.meta.env;
  const setCookies = useCallback(async () => {
    try {
      // use fallback method for local development
      if (VITE_APP_API_URL.includes('localhost') || NODE_ENV === 'test') {
        return fetchCookies();
      }
      
      // Clear any existing CDN cookies (both CloudFront and GCP) before setting new ones
      allCookieNames.forEach(cookieName => {
        setCookie(cookieName, '', { 
          ...getCloudfrontSignedCookieOptions('/'), 
          expires: new Date(0) // Expire immediately
        });
      });
      
      const token = await getAccessTokenSilently();
      const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/cloudfront-cookies-set`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      // Check if the response set cookies automatically (for GCP CDN)
      // The browser will automatically store any Set-Cookie headers from the response
      if (response.ok) {
        return response;
      } else {
        throw new Error(`Failed to set cookies: ${response.status}`);
      }
    } catch (error) {
      // use fallback method for local development
      Sentry.captureMessage(`Failed to call cloudfront-cookies-set`, {
        user: { sub: user?.sub },
        extra: { error: error instanceof Error ? error.message : String(error) },
      });

      return fetchCookies();
    }
  }, [getAccessTokenSilently, fetchCookies, user?.sub, allCookieNames, setCookie, getCloudfrontSignedCookieOptions]);
  return { fetchCookies, setCookies };
};
